import React, { ChangeEvent, ReactElement } from 'react';

import { CompanySettingsFull } from '../utils/GeneralSettingsUtils';
import { CustomInfoCard } from './GeneralSettingsFields.styles';
import {
	InputSelect,
	InputText,
	STYLOVYZE_FLAGS,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { BasemapOrigin } from '@innovyze/stylovyze';
import { selectThemeListForCompany } from '@Selectors/mapManagement.selectors';
import { useFormikContext } from 'formik';
import { useGlobalization } from '@Translations';
import { useSelector } from 'react-redux';
import useSetGeneralSettingsFields from '../utils/useSetGeneralSettingsFields';
import { InputAdornment } from '@mui/material';
import { useCompanySettings } from '@Selectors/companySettings.selectors';

const DefaultMapSettings = (): ReactElement => {
	const { t } = useGlobalization();
	const { values } = useFormikContext<CompanySettingsFull>();
	const setGeneralSettingsFields = useSetGeneralSettingsFields();
	const {
		companySettings: { mapSettings },
	} = useCompanySettings();
	const companyThemeList = useSelector(selectThemeListForCompany);
	const showArcGISBasemaps = !!useIsFeatureEnabled(
		STYLOVYZE_FLAGS.ARCGIS_BASEMAPS,
	);
	const onDefaultThemeChange = (e: ChangeEvent<HTMLInputElement>) => {
		const theme = e.target.value;
		setGeneralSettingsFields({
			mapSettings: {
				...values.mapSettings,
				defaultTheme: theme,
			},
		});
	};

	const onBasemapOriginChange = (e: ChangeEvent<HTMLInputElement>) => {
		setGeneralSettingsFields({
			mapSettings: {
				...values.mapSettings,
				basemapOrigin: e.target.value as BasemapOrigin,
			},
		});
	};

	const onSearchRadiusChange = (e: ChangeEvent<HTMLInputElement>) => {
		setGeneralSettingsFields({
			mapSettings: {
				...values.mapSettings,
				searchRadius: +e.target.value,
			},
		});
	};

	return (
		<CustomInfoCard fullHeight title={t('Map Settings')}>
			<InputSelect
				menuItems={
					companyThemeList?.map(theme => ({
						key: theme.id,
						value: theme.id,
						label:
							theme.name ??
							(theme.id === '1' ? 'Default' : undefined),
					})) ?? []
				}
				fieldAttrs={{ name: 'mapSettings.defaultTheme' }}
				selectProps={{ label: t('Default Map Theme') }}
				dataCy="mapDefaultTheme"
				onChange={onDefaultThemeChange}
			/>
			{showArcGISBasemaps && (
				<InputSelect
					menuItems={[
						{
							key: 'mapbox',
							value: 'mapbox',
							label: 'Mapbox',
						},
						{
							key: 'arcgis',
							value: 'arcgis',
							label: 'Esri',
						},
					]}
					fieldAttrs={{
						name: 'mapSettings.basemapOrigin',
						defaultValue: 'mapbox',
					}}
					selectProps={{
						label: t('Default Basemaps'),
						defaultValue: mapSettings?.basemapOrigin ?? 'mapbox',
					}}
					dataCy="mapDefaultBasemap"
					onChange={onBasemapOriginChange}
				/>
			)}
			<InputText
				fieldAttrs={{
					name: 'mapSettings.searchRadius',
				}}
				textField={{
					label: t('Map Search Radius'),
					type: 'number',
					fullWidth: true,
					value: parseInt(
						`${values.mapSettings?.searchRadius}` || '5',
					),
					InputProps: {
						inputProps: {
							dataCy: 'searchRadius',
							min: 5,
							pattern: /[0-9]/,
						},
						endAdornment: (
							<InputAdornment position="end">
								{values.UOM === 'Metric' ? t('km') : t('miles')}
							</InputAdornment>
						),
					},
				}}
				onDebounceChange={onSearchRadiusChange}
			/>
		</CustomInfoCard>
	);
};

export default DefaultMapSettings;
